import React, { useContext, useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DataContext } from "../../newComponents/context/DataContext";
import { getSchedulingBaseUrl, getReelServiceBaseUrl } from "../../config/apiConfig";
import {
  FaArrowLeft,
  FaUserCircle,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaInstagram,
  FaImage,
  FaFileAlt,
  FaExternalLinkAlt,
  FaFilePdf,
  FaFileImage,
  FaFileVideo,
  FaFileAudio,
  FaFileWord,
  FaPaperclip,
  FaFilm,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSpinner,
} from "react-icons/fa";

const apiDomain = process.env.REACT_APP_API_DOMAIN;

// =============================================================================
// Helpers
// =============================================================================

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(typeof v === "number" && v < 1e12 ? v * 1000 : v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const filterScheduledPostsByEvent = (posts, eventId, eventName) => {
  if (!Array.isArray(posts)) return [];
  const evIdStr = String(eventId);
  const normalizedEventName = eventName ? String(eventName).toLowerCase().trim() : "";
  
  return posts.filter((post) => {
    const url = post.preview_url ? String(post.preview_url) : "";
    const matchesUrl =
      url.includes(`/${evIdStr}/`) ||
      url.includes(`%2F${evIdStr}%2F`) ||
      url.includes(`_${evIdStr}_`);
    
    if (matchesUrl) return true;

    const caption = post.caption ? String(post.caption).toLowerCase() : "";
    if (caption.includes(`event ${evIdStr}`) || caption.includes(`#${evIdStr}`)) {
      return true;
    }
    
    if (normalizedEventName && normalizedEventName.length > 3 && caption.includes(normalizedEventName)) {
      return true;
    }

    return false;
  });
};

const fileIcon = (fileType) => {
  const t = String(fileType || "").toUpperCase();
  if (t === "PDF") return <FaFilePdf className="text-red-500" />;
  if (["JPEG", "JPG", "PNG", "GIF", "WEBP"].includes(t))
    return <FaFileImage className="text-green-500" />;
  if (["MP4", "MOV", "AVI", "WEBM"].includes(t))
    return <FaFileVideo className="text-purple-500" />;
  if (["MP3", "WAV", "AAC"].includes(t))
    return <FaFileAudio className="text-orange-500" />;
  if (["DOCX", "DOC"].includes(t)) return <FaFileWord className="text-blue-500" />;
  return <FaFileAlt className="text-gray-400" />;
};

// Resolve participant's RTDB-uid form used by the admin participation-media
// endpoint. The map stores the bare numeric id, but our backend strips
// "instagram:" / "facebook:" prefixes server-side, so any form works.
const resolveUserKey = (p) =>
  p?.facebookId || p?.userId || p?.instagramBusinessAccountId || "";

// =============================================================================
// Page
// =============================================================================

const ParticipantDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { participant, allEventsSummary, fetchAllEventNames } = useContext(DataContext);

  const [participantData, setParticipantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null); // /api/v2/userinformation
  const [perEvent, setPerEvent] = useState({}); // { [eventId]: { eventInfo, attachments, media, loading } }
  const [expanded, setExpanded] = useState({}); // { [eventId]: bool } — show details inline
  // Manual Instagram-handle editor (for users whose handle wasn't auto-captured)
  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameInput, setUsernameInput] = useState("");
  const [savingUsername, setSavingUsername] = useState(false);
  const [localUsername, setLocalUsername] = useState(null);

  // Resolve the participant. Fast path: the in-memory list (instant when the
  // admin clicked through from the table). On a hard RELOAD or a deep link the
  // context list is still empty, so fall back to fetching the list directly —
  // otherwise the page wrongly flashed "Participant not found". Only surface
  // "not found" after the direct fetch also comes back without a match.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    if (Array.isArray(participant) && participant.length > 0) {
      const found = participant.find(
        (p) => p.userId === userId || p.facebookId === userId
      );
      if (found) {
        setParticipantData(found);
        setLoading(false);
        return;
      }
    }

    // Direct fetch fallback (reload / deep link / row not yet in context).
    (async () => {
      try {
        const res = await axios.get(`${apiDomain}/api/v2/exploreparticipants`, {
          params: { pageSize: 2000, t: Date.now() },
        });
        if (cancelled) return;
        const raw = res.data;
        const list = Array.isArray(raw)
          ? raw
          : raw?.participants || raw?.data || raw?.result || [];
        const found = list.find(
          (p) => p.userId === userId || p.facebookId === userId
        );
        if (found) setParticipantData(found);
        setLoading(false);
      } catch (_) {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [participant, userId]);

  // Fetch canonical registration data (email/mobile/address/dob/etc) which
  // /api/v2/exploreparticipants doesn't include. The endpoint accepts a bare
  // numeric id and tries instagram:/facebook:/google: prefixes server-side.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${apiDomain}/api/v2/userinformation`, {
          params: { userID: userId, t: Date.now() },
        });
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [res.data];
        const u = list?.[0];
        if (u && (u.email || u.mobile_no || u.city || u.state)) {
          setUserInfo(u);
        }
      } catch (_) {
        // No info on file — leave userInfo null, UI shows "Not provided".
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Make sure event-name list is hydrated so we can show names instead of ids.
  useEffect(() => {
    if (!allEventsSummary || allEventsSummary.length === 0) {
      fetchAllEventNames?.();
    }
  }, [allEventsSummary, fetchAllEventNames]);

  // Item (2026-06-04): eagerly resolve which of THIS participant's events
  // require document uploads, plus how many files they've submitted — so the
  // collapsed event rows can show a "Requires files" badge + an uploaded/X
  // count + a jump link to that event's attachments page, without the admin
  // having to expand each event first.
  const [reqFileInfo, setReqFileInfo] = useState({}); // evId -> {requiredCount, uploadedCount, eventName, requiredFiles}
  useEffect(() => {
    const details = participantData?.participationDetails;
    if (!details || details.length === 0) return;
    const userKey = resolveUserKey(participantData);
    if (!userKey) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${apiDomain}/api/v2/attachment/events-with-required-files`,
          { params: { filter: "all" } }
        );
        if (cancelled) return;
        const reqById = {};
        (res.data?.events || []).forEach((e) => {
          reqById[String(e.eventId)] = e;
        });
        const mine = [...new Set(details.map((d) => String(d.event_id)))].filter(
          (id) => reqById[id]
        );
        if (mine.length === 0) return;
        const seed = {};
        mine.forEach((id) => {
          const e = reqById[id];
          seed[id] = {
            requiredCount: Array.isArray(e.requiredFiles) ? e.requiredFiles.length : 0,
            requiredFiles: e.requiredFiles || [],
            eventName: e.eventName || `Event #${id}`,
            uploadedCount: null,
          };
        });
        if (!cancelled) setReqFileInfo(seed);
        // Fill the per-event uploaded counts for this user.
        await Promise.all(
          mine.map(async (id) => {
            try {
              const a = await axios.get(`${apiDomain}/api/v2/attachment/get`, {
                params: { eventId: id, userId: userKey },
              });
              const cnt = Array.isArray(a.data?.attachments)
                ? a.data.attachments.length
                : 0;
              if (!cancelled)
                setReqFileInfo((prev) => ({
                  ...prev,
                  [id]: { ...prev[id], uploadedCount: cnt },
                }));
            } catch (_) {
              /* leave uploadedCount null */
            }
          })
        );
      } catch (_) {
        /* no required-files info — rows just won't show the badge */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [participantData]);

  // Index events by id for O(1) name lookup.
  const eventsById = useMemo(() => {
    const out = {};
    (allEventsSummary || []).forEach((ev) => {
      const id = ev.eventID || ev.eventId || ev.event_id || ev.id;
      if (id != null) out[String(id)] = ev;
    });
    return out;
  }, [allEventsSummary]);

  // Load per-event extras lazily when the user expands a card. Keeps initial
  // render fast even for participants with 20+ events in history.
  const ensureLoaded = async (eventId) => {
    if (perEvent[eventId]?.loading || perEvent[eventId]?.loaded) return;
    setPerEvent((prev) => ({
      ...prev,
      [eventId]: { ...(prev[eventId] || {}), loading: true },
    }));
    const userKey = resolveUserKey(participantData);

    // Generate query IDs for scheduling API to handle ID mapping mismatches
    const queryIds = new Set();
    if (userKey) {
      queryIds.add(userKey);
      if (userKey.includes(":")) {
        const parts = userKey.split(":");
        const raw = parts[1];
        if (raw) {
          queryIds.add(raw);
          queryIds.add(`instagram:${raw}`);
          queryIds.add(`facebook:${raw}`);
        }
      } else {
        queryIds.add(`instagram:${userKey}`);
        queryIds.add(`facebook:${userKey}`);
      }
    }

    const schedulingBase = getSchedulingBaseUrl();
    const uniqueIds = Array.from(queryIds);
    const schedulingPromises = uniqueIds.map((id) =>
      axios.get(`${schedulingBase}/api/v2/facebook/media/scheduled`, {
        params: { user_id: id, limit: 50 },
      })
    );

    const [evResp, attResp, mediaResp, ...schedResps] = await Promise.allSettled([
      eventsById[eventId]
        ? Promise.resolve({ data: [eventsById[eventId]] })
        : axios.get(`${apiDomain}/api/v2/getevent`, { params: { eventId } }),
      axios.get(`${apiDomain}/api/v2/attachment/get`, {
        params: { eventId, userId: userKey },
      }),
      axios.get(`${apiDomain}/api/v2/admin/participant/media`, {
        params: { user_id: userKey, event_id: eventId },
      }),
      ...schedulingPromises
    ]);

    const ev = evResp.status === "fulfilled"
      ? (Array.isArray(evResp.value.data) ? evResp.value.data[0] : evResp.value.data)
      : null;
    const attachments =
      attResp.status === "fulfilled" ? attResp.value.data?.attachments || [] : [];
    const media =
      mediaResp.status === "fulfilled" ? mediaResp.value.data?.media || [] : [];

    // Parse and merge scheduled posts
    const scheduledPostsList = [];
    schedResps.forEach((resp) => {
      if (resp.status === "fulfilled" && resp.value?.data?.data?.scheduled_posts) {
        scheduledPostsList.push(...resp.value.data.data.scheduled_posts);
      }
    });

    // Deduplicate by post_id
    const seenPostIds = new Set();
    const deduplicatedScheduled = [];
    scheduledPostsList.forEach((post) => {
      if (post && post.post_id && !seenPostIds.has(post.post_id)) {
        seenPostIds.add(post.post_id);
        deduplicatedScheduled.push(post);
      }
    });

    setPerEvent((prev) => ({
      ...prev,
      [eventId]: {
        eventInfo: ev,
        attachments,
        media,
        scheduled: deduplicatedScheduled,
        loading: false,
        loaded: true,
      },
    }));
  };

  const toggleExpanded = (eventId) => {
    const next = !expanded[eventId];
    setExpanded((prev) => ({ ...prev, [eventId]: next }));
    if (next) ensureLoaded(eventId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-xl text-gray-600">
        Loading…
      </div>
    );
  }

  if (!participantData) {
    return (
      <div className="lg:mr-8 mx-4 my-10">
        <button
          onClick={() => navigate("/participants")}
          className="flex items-center gap-2 mb-6 text-blue-600 hover:text-blue-800 font-semibold"
        >
          <FaArrowLeft /> Back to Participants
        </button>
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 text-center">
          <p className="text-red-800 font-semibold">Participant not found</p>
        </div>
      </div>
    );
  }

  const ig = participantData.instagramUser || {};
  const igUsername = localUsername || ig.username || participantData.instagram_username || "";
  const participations = participantData.participationDetails || [];

  const saveUsername = async () => {
    const handle = usernameInput.trim().replace(/^@/, "");
    if (!handle) return;
    setSavingUsername(true);
    try {
      await axios.post(`${apiDomain}/api/v2/admin/participant/set-username`, {
        user_id: participantData.userId || participantData.facebookId || userId,
        username: handle,
      });
      setLocalUsername(handle);
      setEditingUsername(false);
    } catch (e) {
      alert("Failed to save username. Please try again.");
    } finally {
      setSavingUsername(false);
    }
  };

  return (
    <div className="lg:mr-8 mx-4 my-8">
      <button
        onClick={() => navigate("/participants")}
        className="flex items-center gap-2 mb-6 text-blue-600 hover:text-blue-800 font-semibold transition"
      >
        <FaArrowLeft /> Back to Participants
      </button>

      {/* ───── Header ───── */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-full border-4 border-white bg-gray-100 flex items-center justify-center flex-shrink-0 shadow-lg">
              {ig.profile_picture_url ? (
                <img
                  src={ig.profile_picture_url}
                  alt="Profile"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <FaUserCircle className="w-16 h-16 text-gray-400" />
              )}
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold">
                {ig.name || participantData.name || igUsername || "Unknown User"}
              </h1>
              {editingUsername ? (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-blue-100">@</span>
                  <input
                    autoFocus
                    value={usernameInput}
                    onChange={(e) => setUsernameInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveUsername()}
                    placeholder="instagram handle"
                    className="px-2 py-1 rounded text-gray-800 text-sm w-48 outline-none"
                  />
                  <button
                    onClick={saveUsername}
                    disabled={savingUsername}
                    className="bg-white text-blue-600 text-xs font-semibold px-3 py-1 rounded hover:bg-blue-50 disabled:opacity-60"
                  >
                    {savingUsername ? "Saving…" : "Save"}
                  </button>
                  <button
                    onClick={() => setEditingUsername(false)}
                    className="text-blue-100 text-xs hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              ) : igUsername ? (
                <span className="mt-2 inline-flex items-center gap-2">
                  <a
                    href={`https://instagram.com/${igUsername}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-100 hover:underline"
                  >
                    <FaInstagram /> @{igUsername}
                  </a>
                  <button
                    onClick={() => { setUsernameInput(igUsername); setEditingUsername(true); }}
                    title="Edit handle"
                    className="text-blue-200 text-xs hover:text-white hover:underline"
                  >
                    edit
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => { setUsernameInput(""); setEditingUsername(true); }}
                  className="mt-2 inline-flex items-center gap-1 text-blue-100 hover:text-white hover:underline text-sm"
                >
                  <FaInstagram /> Set Instagram handle
                </button>
              )}
              <p className="text-blue-100 text-sm mt-1">
                User ID: {participantData.userId || participantData.facebookId}
              </p>
            </div>
          </div>
        </div>

        {/* ───── Stats ───── */}
        <div className="p-6 lg:p-8">
          <div className="grid md:grid-cols-4 gap-3 mb-8">
            <StatCard
              label="Participation Count"
              value={participantData.participationCount || participations.length}
              color="blue"
            />
            <StatCard label="Instagram Followers" value={ig.followers_count || 0} color="green" />
            <StatCard label="Following" value={ig.follows_count || 0} color="purple" />
            <StatCard label="Media Count" value={ig.media_count || 0} color="orange" />
          </div>

          {/* ───── User Info ───── */}
          <h2 className="text-xl font-bold text-gray-800 mb-3 border-b-2 border-blue-500 pb-2">
            User Information
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mb-8 text-sm">
            <InfoCell
              label="Email"
              value={userInfo?.email || participantData.email || "Not provided"}
            />
            <InfoCell
              label="Mobile"
              value={userInfo?.mobile_no || participantData.mobile_no || "Not provided"}
            />
            <InfoCell
              label="Full Name (registration)"
              value={userInfo?.name || participantData.name || "Not provided"}
            />
            <InfoCell
              label="Gender / Age"
              value={
                [userInfo?.gender, userInfo?.age]
                  .filter((v) => v !== undefined && v !== null && v !== "" && v !== 0)
                  .join(" · ") || "Not provided"
              }
            />
            <InfoCell label="Date of Birth" value={userInfo?.dob || "Not provided"} />
            <InfoCell
              label="Address"
              value={
                [
                  userInfo?.building_no,
                  userInfo?.home_street,
                  userInfo?.city,
                  userInfo?.state,
                  userInfo?.pin_code,
                ]
                  .filter((v) => v && String(v).trim() !== "")
                  .join(", ") || "Not provided"
              }
              fullWidth
            />
            <InfoCell
              label="Instagram Account ID"
              value={participantData.instagramBusinessAccountId || "Not linked"}
              mono
            />
            <InfoCell
              label="User ID (Facebook)"
              value={participantData.userId || participantData.facebookId}
              mono
            />
            <InfoCell label="Joining Date" value={fmtDate(participantData.joiningTime)} />
            <InfoCell
              label="Referral Code"
              value={userInfo?.referral_code || "—"}
              mono
            />
            <InfoCell
              label="Website"
              value={
                ig.website ? (
                  <a
                    href={ig.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {ig.website}
                  </a>
                ) : (
                  "Not provided"
                )
              }
            />
            {ig.biography && (
              <InfoCell label="Biography" value={ig.biography} fullWidth />
            )}
          </div>

          {/* ───── Event Participation ───── */}
          <h2 className="text-xl font-bold text-gray-800 mb-3 border-b-2 border-green-500 pb-2 flex items-center justify-between">
            <span>Event Participation ({participations.length})</span>
            {participations.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  const allOpen = participations.every((p) => expanded[p.event_id]);
                  if (allOpen) {
                    setExpanded({});
                  } else {
                    const next = {};
                    participations.forEach((p) => {
                      next[p.event_id] = true;
                      ensureLoaded(p.event_id);
                    });
                    setExpanded(next);
                  }
                }}
                className="text-xs font-medium text-blue-600 hover:underline"
              >
                {participations.every((p) => expanded[p.event_id])
                  ? "Collapse all"
                  : "Expand all"}
              </button>
            )}
          </h2>

          {participations.length === 0 ? (
            <p className="text-gray-500 italic">No participation records.</p>
          ) : (
            <div className="space-y-3">
              {participations.map((detail, idx) => {
                const evId = String(detail.event_id);
                const ev = eventsById[evId] || perEvent[evId]?.eventInfo || null;
                const isOpen = !!expanded[evId];
                const eventName = ev?.eventName || ev?.event_name || `Event #${evId}`;
                const eventType = ev?.eventType || ev?.eventtype || "";
                return (
                  <EventCard
                    key={`${evId}-${idx}`}
                    eventId={evId}
                    eventName={eventName}
                    eventType={eventType}
                    detail={detail}
                    isOpen={isOpen}
                    onToggle={() => toggleExpanded(evId)}
                    extras={perEvent[evId]}
                    reqInfo={reqFileInfo[evId]}
                    igUsername={igUsername}
                    userKey={resolveUserKey(participantData)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Sub-components
// =============================================================================

const StatCard = ({ label, value, color }) => {
  const palette = {
    blue: "bg-blue-50 border-blue-200 text-blue-600",
    green: "bg-green-50 border-green-200 text-green-600",
    purple: "bg-purple-50 border-purple-200 text-purple-600",
    orange: "bg-orange-50 border-orange-200 text-orange-600",
  }[color] || "bg-gray-50 border-gray-200 text-gray-600";
  return (
    <div className={`${palette} border rounded-lg p-4`}>
      <p className="text-gray-600 text-xs font-semibold uppercase tracking-wide">
        {label}
      </p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
};

const InfoCell = ({ label, value, mono, fullWidth }) => (
  <div className={fullWidth ? "md:col-span-2" : ""}>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
    <p className={`text-gray-800 mt-0.5 ${mono ? "font-mono text-sm break-all" : ""}`}>
      {value}
    </p>
  </div>
);

// ── ReelJobsTab: fetches & renders all reel job attempts for a user+event ──
const REEL_STATUS_STYLES = {
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  running: "bg-amber-50 text-amber-700 border-amber-200",
  queued: "bg-blue-50 text-blue-700 border-blue-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-slate-50 text-slate-500 border-slate-200",
};

const ReelJobsTab = ({ userId, eventId }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !eventId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const reelBase = getReelServiceBaseUrl();
    // Try multiple user ID forms the reel service may have indexed under
    const variants = new Set([userId]);
    if (userId.includes(":")) {
      const raw = userId.split(":")[1];
      if (raw) {
        variants.add(raw);
        variants.add(`instagram:${raw}`);
        variants.add(`facebook:${raw}`);
      }
    } else {
      variants.add(`instagram:${userId}`);
      variants.add(`facebook:${userId}`);
    }

    Promise.allSettled(
      Array.from(variants).map((uid) =>
        axios.get(`${reelBase}/api/v2/reel/jobs`, {
          params: { user_id: uid, event_id: eventId },
        })
      )
    ).then((results) => {
      if (cancelled) return;
      const seen = new Set();
      const merged = [];
      results.forEach((r) => {
        if (r.status !== "fulfilled") return;
        const list = r.value?.data?.jobs || r.value?.data?.data || r.value?.data || [];
        (Array.isArray(list) ? list : []).forEach((j) => {
          const key = j.job_id || j.id || JSON.stringify(j);
          if (!seen.has(key)) { seen.add(key); merged.push(j); }
        });
      });
      // Sort newest first
      merged.sort((a, b) => {
        const ta = a.created_at || a.queued_at || 0;
        const tb = b.created_at || b.queued_at || 0;
        return new Date(tb) - new Date(ta);
      });
      setJobs(merged);
      setLoading(false);
    }).catch((e) => {
      if (!cancelled) { setError(e.message); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [userId, eventId]);

  if (loading) return (
    <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
      <FaSpinner className="animate-spin" /> Loading reel jobs…
    </div>
  );

  if (error) return (
    <div className="p-4 text-sm text-rose-600 bg-rose-50 rounded">
      Could not load reel jobs: {error}
    </div>
  );

  if (jobs.length === 0) return (
    <p className="p-4 text-sm text-gray-500 italic">
      No reel composition attempts found for this event.
    </p>
  );

  return (
    <div className="space-y-3 p-1">
      {jobs.map((job, i) => {
        const st = (job.status || "").toLowerCase();
        const stStyle = REEL_STATUS_STYLES[st] || "bg-gray-50 text-gray-700 border-gray-200";
        const isDone = st === "done" || st === "completed";
        const isFailed = st === "failed";
        const igPublished = job.instagram_post_id || job.ig_post_id || job.published_ig_id;
        const igScheduled = job.scheduled_post_id || job.ig_scheduled_id;
        const compositeUrl = job.composite_url || job.output_url;

        return (
          <div key={job.job_id || job.id || i}
            className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition">
            {/* Header row */}
            <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b">
              <FaFilm className="text-pink-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-mono text-slate-500 truncate">
                  {job.job_id || job.id || `Attempt #${i + 1}`}
                </span>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${stStyle}`}>
                {job.status || "unknown"}
              </span>
            </div>

            {/* Body */}
            <div className="px-3 py-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-slate-600">
              {(job.created_at || job.queued_at) && (
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">Submitted</p>
                  <p>{fmtDate(job.created_at || job.queued_at)}</p>
                </div>
              )}
              {job.completed_at && (
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">Completed</p>
                  <p>{fmtDate(job.completed_at)}</p>
                </div>
              )}
              {typeof job.progress === "number" && !isDone && (
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">Progress</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-pink-500 rounded-full"
                        style={{ width: `${Math.round(job.progress * 100)}%` }} />
                    </div>
                    <span className="font-mono">{Math.round(job.progress * 100)}%</span>
                  </div>
                </div>
              )}
            </div>

            {/* Instagram publish status */}
            <div className="px-3 pb-2 flex flex-wrap gap-2">
              {igPublished ? (
                <span className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold">
                  <FaCheckCircle size={10} />
                  Published to Instagram
                  {typeof igPublished === "string" && igPublished.length > 5 && (
                    <span className="font-mono ml-1 text-[9px]">({igPublished.slice(-8)})</span>
                  )}
                </span>
              ) : igScheduled ? (
                <span className="inline-flex items-center gap-1 text-[11px] bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-semibold">
                  <FaClock size={10} />
                  Scheduled for Instagram
                </span>
              ) : isDone ? (
                <span className="inline-flex items-center gap-1 text-[11px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                  <FaClock size={10} />
                  Reel ready — not yet published
                </span>
              ) : isFailed ? (
                <span className="inline-flex items-center gap-1 text-[11px] bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-full">
                  <FaTimesCircle size={10} />
                  Composition failed
                  {job.error && <span className="ml-1 italic">({job.error})</span>}
                </span>
              ) : null}

              {compositeUrl && (
                <a href={compositeUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full hover:bg-purple-100 transition">
                  <FaFileVideo size={9} /> View Reel Video <FaExternalLinkAlt size={8} />
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Tab-based EventCard ──────────────────────────────────────────────────────
const EVENT_TABS = ["Info", "Participation Post", "Reel Jobs"];

const EventCard = ({
  eventId,
  eventName,
  eventType,
  detail,
  isOpen,
  onToggle,
  extras,
  reqInfo,
  igUsername,
  userKey,
}) => {
  const [activeTab, setActiveTab] = useState("Info");
  const attachments = extras?.attachments || [];
  const media = extras?.media || [];
  const scheduled = extras?.scheduled || [];
  const filteredScheduled = useMemo(() =>
    filterScheduledPostsByEvent(scheduled, eventId, eventName),
    [scheduled, eventId, eventName]
  );
  const requiredFiles =
    reqInfo?.requiredFiles ||
    extras?.eventInfo?.additionalData?.requiredFiles ||
    extras?.eventInfo?.additionalData?.required_files || [];
  const requiredFilesEnabled = !!reqInfo || !!(
    extras?.eventInfo?.additionalData?.requiredFilesEnabled ||
    extras?.eventInfo?.additionalData?.required_files_enabled
  );

  const displayName =
    eventName && !eventName.startsWith("Event #")
      ? eventName
      : `(Unnamed event #${eventId})`;

  return (
    <div className="bg-gray-50 border border-gray-300 rounded-lg overflow-hidden hover:shadow-md transition">
      {/* Collapsed header */}
      <div className="flex items-center justify-between gap-3 p-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Link to={`/event-form/${eventId}`}
              className="font-semibold text-gray-800 hover:text-blue-600 hover:underline truncate"
              title="Open this event for editing"
              onClick={(e) => e.stopPropagation()}>
              {displayName}
            </Link>
            {eventType && (
              <span className="text-[10px] uppercase tracking-wider bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                {eventType}
              </span>
            )}
            <Link to={`/event-form/${eventId}`} onClick={(e) => e.stopPropagation()}
              className="text-[10px] tracking-wider bg-blue-100 text-blue-700 hover:bg-blue-200 px-1.5 py-0.5 rounded"
              title="Open event edit page">
              #{eventId}
            </Link>
            {reqInfo && (
              <>
                <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                  <FaPaperclip size={9} /> Requires files
                </span>
                {reqInfo.uploadedCount != null && (
                  <span className={`text-[10px] tracking-wider px-1.5 py-0.5 rounded ${
                    reqInfo.uploadedCount > 0 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                  }`}>
                    {reqInfo.uploadedCount}/{reqInfo.requiredCount} uploaded
                  </span>
                )}
                <Link to={`/event-attachments/${eventId}`}
                  state={{ eventName: displayName, requiredFiles: reqInfo.requiredFiles }}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[10px] tracking-wider bg-blue-50 text-blue-700 hover:bg-blue-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                  Attachments <FaExternalLinkAlt size={8} />
                </Link>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1">
              <FaCalendarAlt /> {fmtDate(detail.participation_time)}
            </span>
            {(detail.city || detail.state) && (
              <span className="inline-flex items-center gap-1">
                <FaMapMarkerAlt />
                {[detail.city, detail.state, detail.pincode].filter(Boolean).join(", ")}
              </span>
            )}
          </div>
        </div>
        <button type="button" onClick={onToggle}
          className="text-gray-400 hover:text-gray-700 text-sm font-medium px-2 py-1 rounded hover:bg-gray-100">
          {isOpen ? "Hide" : "View"}
        </button>
      </div>

      {isOpen && (
        <div className="border-t bg-white">
          {/* Tab bar */}
          <div className="flex border-b">
            {EVENT_TABS.map((tab) => (
              <button key={tab} type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-xs font-semibold tracking-wide transition border-b-2 -mb-px ${
                  activeTab === tab
                    ? "border-pink-500 text-pink-600 bg-pink-50"
                    : "border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                }`}>
                {tab === "Reel Jobs" && <FaFilm className="inline mr-1" size={10} />}
                {tab}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="p-4">
            {/* ── INFO ── */}
            {activeTab === "Info" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <Meta label="Generic Category" value={detail.generic_category} />
                  <Meta label="Age Category" value={detail.age_category} />
                  <Meta label="Gender" value={detail.gender_category} />
                  <Meta label="Followers" value={detail.followers_count_category} />
                  <Meta label="Entity" value={detail.entity_name} />
                  <Meta label="Entity Type" value={detail.entity_type} />
                  <Meta label="International" value={detail.international ? "Yes" : "No (Domestic)"} />
                </div>

                {/* Required Files */}
                {(attachments.length > 0 || requiredFilesEnabled) && (
                  <div className="border rounded overflow-hidden">
                    <div className="px-3 py-2 border-b bg-gray-50 flex items-center gap-2">
                      <FaPaperclip className="text-amber-500" size={12} />
                      <span className="text-xs font-bold uppercase tracking-wide text-gray-600">
                        Required Files
                        {requiredFilesEnabled && (
                          <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">mandatory</span>
                        )}
                        <span className="ml-2 text-gray-400">
                          ({attachments.length}{requiredFiles.length > 0 ? ` / ${requiredFiles.length}` : ""})
                        </span>
                      </span>
                    </div>
                    <div className="p-3">
                      {extras?.loading ? (
                        <p className="text-sm text-gray-500">Loading uploads…</p>
                      ) : attachments.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">
                          {requiredFilesEnabled ? "User has not uploaded any required documents." : "No files uploaded."}
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {attachments.map((a, i) => (
                            <li key={`${a.fileName}-${i}`}
                              className="flex items-center gap-3 text-sm border-b last:border-0 pb-2 last:pb-0">
                              {fileIcon(a.fileType)}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-800 truncate">{a.fileName || a.fileType}</div>
                                <div className="text-xs text-gray-500">
                                  {a.fileType || "FILE"} · {a.uploadedAt ? fmtDate(a.uploadedAt) : "—"}
                                  {a.description ? ` · ${a.description}` : ""}
                                </div>
                              </div>
                              {a.fileUrl && (
                                <a href={a.fileUrl} target="_blank" rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                                  Open <FaExternalLinkAlt size={10} />
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── PARTICIPATION POST ── */}
            {activeTab === "Participation Post" && (
              <div className="space-y-4">
                {/* Submitted media */}
                <div className="border rounded overflow-hidden">
                  <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-600">
                      Posts Submitted ({media.length})
                    </span>
                    {igUsername && (
                      <a href={`https://instagram.com/${igUsername}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                        Open IG profile <FaExternalLinkAlt size={10} />
                      </a>
                    )}
                  </div>
                  <div className="p-3">
                    {extras?.loading ? (
                      <p className="text-sm text-gray-500">Loading posts…</p>
                    ) : media.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No posts indexed for this event.</p>
                    ) : (
                      <ul className="space-y-2">
                        {media.map((m) => (
                          <li key={m.media_id}
                            className="flex items-center gap-3 text-sm border-b last:border-0 pb-2 last:pb-0">
                            <FaImage className="text-purple-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-800 truncate">
                                {m.title || "(no caption)"}
                                {m.username && (
                                  <span className="ml-2 text-xs font-normal text-gray-500">@{m.username}</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                                <span className="font-mono">{m.media_id}</span>
                                {m.post_type && <span className="bg-gray-100 px-1.5 py-0.5 rounded">{m.post_type}</span>}
                                {m.created_at && <span>{fmtDate(m.created_at)}</span>}
                              </div>
                            </div>
                            {m.permalink && (
                              <a href={m.permalink} target="_blank" rel="noopener noreferrer"
                                className="flex-shrink-0 text-xs text-blue-600 hover:underline inline-flex items-center gap-1 whitespace-nowrap">
                                Open post <FaExternalLinkAlt size={9} />
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Scheduled posts */}
                {filteredScheduled.length > 0 && (
                  <div className="border rounded overflow-hidden shadow-sm">
                    <div className="px-3 py-2 border-b bg-indigo-50/50 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                      <span className="text-xs font-bold uppercase tracking-wide text-indigo-700">
                        Scheduled Posts ({filteredScheduled.length})
                      </span>
                    </div>
                    <div className="p-3 space-y-2.5">
                      {filteredScheduled.map((post) => {
                        const sc = {
                          scheduled: "bg-blue-50 text-blue-700 border-blue-200",
                          published: "bg-emerald-50 text-emerald-700 border-emerald-200",
                          completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
                          failed: "bg-rose-50 text-rose-700 border-rose-200",
                          processing: "bg-amber-50 text-amber-700 border-amber-200",
                          cancelled: "bg-slate-50 text-slate-600 border-slate-200",
                        }[post.status?.toLowerCase()] || "bg-gray-50 text-gray-700 border-gray-200";
                        return (
                          <div key={post.post_id}
                            className="flex items-start gap-3 p-2.5 border rounded-lg hover:bg-slate-50 transition group">
                            <div className="flex-shrink-0">
                              {post.preview_url ? (
                                <a href={post.preview_url} target="_blank" rel="noopener noreferrer"
                                  className="block relative rounded overflow-hidden border border-slate-200 hover:shadow">
                                  <img src={post.preview_url} alt="Preview"
                                    className="w-12 h-12 object-cover group-hover:scale-105 transition-transform duration-150" />
                                </a>
                              ) : (
                                <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center border">
                                  <FaImage className="text-slate-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                                <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${sc}`}>
                                  {post.status || "Unknown"}
                                </span>
                                {post.media_type && (
                                  <span className="text-[9px] bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded uppercase font-medium">
                                    {post.media_type}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-800 font-medium mb-0.5 line-clamp-2">
                                {post.caption || <span className="text-slate-400 italic">No caption</span>}
                              </p>
                              {post.schedule_time && (
                                <span className="text-[10px] text-slate-500 flex items-center gap-0.5">
                                  <FaCalendarAlt className="text-slate-400" />
                                  <span className="font-semibold text-slate-600">Scheduled:</span> {fmtDate(post.schedule_time)}
                                </span>
                              )}
                            </div>
                            {post.preview_url && (
                              <a href={post.preview_url} target="_blank" rel="noopener noreferrer"
                                className="text-slate-400 hover:text-indigo-600 p-1 rounded hover:bg-slate-100 transition self-center flex-shrink-0">
                                <FaExternalLinkAlt size={10} />
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── REEL JOBS ── */}
            {activeTab === "Reel Jobs" && (
              <div>
                <p className="text-xs text-gray-500 mb-3">
                  All reel composition attempts by this participant for this event, newest first.
                </p>
                <ReelJobsTab userId={userKey} eventId={eventId} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Meta = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-gray-800 font-medium">{value || "—"}</p>
  </div>
);

export default ParticipantDetail;

